import {
  LayoutJSON,
  LayoutModel,
  dimension,
  getLayoutComponent,
  isContainer,
  isLayoutComponent,
  uuid,
} from "@vuu-ui/vuu-utils";
import React, { CSSProperties, ReactElement, cloneElement } from "react";
import { TabLabelFactory } from "../stack";
import {
  getPersistentState,
  hasPersistentState,
  setPersistentState,
} from "../use-persistent-state";
import { expandFlex, followPathToParent, getProps, typeOf } from "../utils";
import { layoutType } from "./layoutTypes";

interface ComponentWithId {
  id: string;
  [key: string]: unknown;
}

export const getManagedDimension = (
  style: CSSProperties,
): [dimension, dimension] =>
  style.flexDirection === "column" ? ["height", "width"] : ["width", "height"];

const theKidHasNoStyle: CSSProperties = {};

export const applyLayoutProps = (component: ReactElement, path = "0") => {
  const [layoutProps, children] = getChildLayoutProps(
    typeOf(component) as string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    component.props as any,
    path,
  );
  return React.cloneElement(component, layoutProps, children);
};

export interface LayoutProps extends ComponentWithId {
  active?: number;
  "data-path"?: string;
  children?: ReactElement[];
  column?: boolean;
  dropTarget?: boolean;
  key: string;
  layout?: LayoutJSON;
  path?: string;
  resizeable?: boolean;
  style: CSSProperties;
  type?: string;
  version?: number;
}

/**
 * parse the declarative JSX and clone adding layout attributes
 */
export const cloneElementAddLayoutProps = (
  layoutElement: ReactElement,
  previousLayout?: ReactElement,
): ReactElement => {
  const type = typeOf(layoutElement) as string;
  const [layoutProps, children] = getChildLayoutProps(
    type,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    layoutElement.props as any,
    "0",
    undefined,
    previousLayout,
  );
  return cloneElement(layoutElement, layoutProps, children);
};

export const applyLayout = (
  type: layoutType,
  props: LayoutProps,
  previousLayout?: LayoutModel,
): LayoutModel => {
  const [layoutProps, children] = getChildLayoutProps(
    type,
    props,
    "0",
    undefined,
    previousLayout,
  );
  return {
    ...props,
    ...layoutProps,
    type,
    children,
  };
};

function getLayoutProps(
  type: string,
  props: LayoutProps,
  path = "0",
  parentType: string | null = null,
  previousLayout?: LayoutModel,
): LayoutProps {
  const {
    active: prevActive = 0,
    "data-path": dataPath,
    path: prevPath = dataPath,
    id: prevId,
    style: prevStyle,
  } = getProps(previousLayout);

  const prevMatch = typeOf(previousLayout) === type && path === prevPath;
  const id = prevMatch ? prevId : (props.id ?? uuid());
  const active = type === "Stack" ? (props.active ?? prevActive) : undefined;

  const key = id;
  const style = prevMatch ? prevStyle : getStyle(type, props, parentType);
  return isLayoutComponent(type)
    ? { id, key, path, style, type, active }
    : { id, key, style, "data-path": path };
}

function getChildLayoutProps(
  type: string,
  props: LayoutProps,
  path: string,
  parentType: string | null = null,
  previousLayout?: LayoutModel,
): [LayoutProps, ReactElement[]] {
  const layoutProps = getLayoutProps(
    type,
    props,
    path,
    parentType,
    previousLayout,
  );

  if (props.layout && !previousLayout) {
    return [layoutProps, [layoutFromJson(props.layout, `${path}.0`)]];
  }

  const previousChildren =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (previousLayout as any)?.children ??
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (previousLayout as any)?.props?.children;
  const hasDynamicChildren = props.dropTarget && previousChildren;
  const children = hasDynamicChildren
    ? previousChildren
    : getLayoutChildren(type, props.children, path, previousChildren);
  return [layoutProps, children];
}

function getLayoutChildren(
  type: string,
  children?: ReactElement[],
  path = "0",
  previousChildren?: ReactElement[],
) {
  const kids = Array.isArray(children)
    ? children
    : React.isValidElement(children)
      ? [children]
      : [];
  return isContainer(type)
    ? kids.map((child, i) => {
        const childType = typeOf(child) as string;
        const previousType = typeOf(previousChildren?.[i]);

        if (!previousType || childType === previousType) {
          const [layoutProps, children] = getChildLayoutProps(
            childType,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            child.props as any,
            `${path}.${i}`,
            type,
            previousChildren?.[i],
          );
          return React.cloneElement(child, layoutProps, children);
        }

        return previousChildren?.[i];
      })
    : children;
}

const getStyle = (
  type: string,
  props: LayoutProps,
  parentType?: string | null,
) => {
  let { style = theKidHasNoStyle } = props;
  if (type === "Flexbox") {
    style = {
      flexDirection: props.column ? "column" : "row",
      ...style,
      display: "flex",
    };
  }

  if (style.flex) {
    const { flex, ...otherStyles } = style;
    style = {
      ...otherStyles,
      ...expandFlex(typeof flex === "number" ? flex : 0),
    };
  } else if (parentType === "Stack") {
    style = {
      ...style,
      ...expandFlex(1),
    };
  } else if (
    parentType === "Flexbox" &&
    (style.width || style.height) &&
    style.flexBasis === undefined
  ) {
    style = {
      ...style,
      flexBasis: "auto",
      flexGrow: 0,
      flexShrink: 0,
    };
  }

  return style;
};

export function layoutFromJson(
  { active, id = uuid(), type, children, props, state }: LayoutJSON,
  path: string,
): ReactElement {
  const componentType = type.match(/^[a-z]/) ? type : getLayoutComponent(type);

  if (componentType === undefined) {
    throw Error(
      `layoutUtils unable to create component from JSON, unknown type ${type}`,
    );
  }

  if (state) {
    setPersistentState(id, state);
  }

  return React.createElement(
    componentType,
    {
      active,
      id,
      ...props,
      key: id,
      path,
    },
    children
      ? children.map((child, i) => layoutFromJson(child, `${path}.${i}`))
      : undefined,
  );
}

export function layoutToJSON(component: ReactElement) {
  return componentToJson(component);
}

export function componentToJson(component: ReactElement): LayoutJSON {
  const type = typeOf(component) as string;
  const { id, children, type: _omit, ...props } = getProps(component);

  const state = hasPersistentState(id) ? getPersistentState(id) : undefined;

  return {
    id,
    type,
    props: serializeProps(props as LayoutProps),
    state,
    children: React.Children.map(children, componentToJson),
  };
}

export function serializeProps(props?: LayoutProps) {
  if (props) {
    const { path, ...otherProps } = props;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: { [key: string]: any } = {};
    for (const [key, value] of Object.entries(otherProps)) {
      result[key] = serializeValue(value);
    }
    return result;
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function serializeValue(value: unknown): any {
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    return value;
  } else if (Array.isArray(value)) {
    return value.map(serializeValue);
  } else if (typeof value === "object" && value !== null) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: { [key: string]: any } = {};
    for (const [k, v] of Object.entries(value)) {
      result[k] = serializeValue(v);
    }
    return result;
  }
}

// This is experimental and the only query we support to start off with is
// PARENT_CONTAINER
export type LayoutQuery = "PARENT_CONTAINER";

export const layoutQuery = (
  query: LayoutQuery,
  path?: string,
  layoutRoot?: ReactElement,
) => {
  if (path && layoutRoot) {
    const parentElement = followPathToParent(layoutRoot, path);
    if (parentElement) {
      const { id: parentContainerId } = getProps(parentElement);
      const parentContainerType = typeOf(parentElement);
      return {
        parentContainerId,
        parentContainerType,
      };
    }
    return {
      parentContainerType: "Stack",
      parentContainerId: "blah",
    };
  }
};

export const getDefaultTabLabel: TabLabelFactory = (
  component,
  tabIndex,
  existingLabels = [],
): string => {
  let label =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (component.props as any)?.title ??
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (component.props as any)?.["data-tab-title"] ??
    existingLabels[tabIndex];
  if (label) {
    return label;
  } else {
    let count = tabIndex;
    do {
      label = `Tab ${++count}`;
    } while (existingLabels.includes(label));
    return label;
  }
};
